import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { api, type LookupPublicationResponse } from "@/lib/api";

export type PushStatus = "ok" | "mirakl_only" | "partial";

interface PushPriceModalProps {
  sku: string;
  marketplaceId: string;
  marketplaceLabel: string;
  // Prezzo customer-facing attuale (min su all_prices): cosa il
  // cliente paga davvero oggi. Puo' essere uno sconto attivo.
  myPrice: string | null;
  // "Prezzo predefinito" Mirakl: il campo che il push OF24 sovrascrive,
  // quello visibile come "Prezzo predefinito" sul seller center. Se
  // assente (es. marketplace non-Mirakl) si usa myPrice in modale.
  myDefaultPrice?: string | null;
  suggestedPrice: string;
  expectedMarginPct: string | null;
  onClose: () => void;
  onSuccess: (status: PushStatus) => void;
}

function isPoleepoSkipped(error: string): boolean {
  // Pattern dal backend: "skipped: publication_not_mapped" oppure
  // "skipped: credentials_missing".
  return /^skipped:/i.test(error.trim());
}

function isPublicationNotMapped(error: string): boolean {
  return /skipped:\s*publication_not_mapped/i.test(error.trim());
}

function diagnosisToTone(
  diagnosis: LookupPublicationResponse["diagnosis"],
): "info" | "warning" | "success" {
  switch (diagnosis) {
    case "ok":
    case "found_live_run_sync":
      return "success";
    case "marketplace_not_poleepo_managed":
    case "not_in_cache_run_sync":
      return "info";
    case "sku_in_poleepo_other_marketplaces":
    case "sku_not_in_poleepo":
    case "live_scan_incomplete":
    case "live_unavailable":
      return "warning";
    default:
      return "info";
  }
}

export function PushPriceModal({
  sku,
  marketplaceId,
  marketplaceLabel,
  myPrice,
  myDefaultPrice = null,
  suggestedPrice,
  expectedMarginPct,
  onClose,
  onSuccess,
}: PushPriceModalProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState<boolean>(false);
  const [autoSyncHint, setAutoSyncHint] = useState<boolean>(false);

  const push = useMutation({
    mutationFn: () =>
      api.manualReprice({
        sku,
        marketplace_id: marketplaceId,
        new_price: suggestedPrice,
      }),
    onSuccess: (data) => {
      const poleepoOk = data.poleepo.success;
      const poleepoSkipped =
        !poleepoOk && isPoleepoSkipped(data.poleepo.error);
      const miraklOk = data.mirakl.success;

      // Auto-sync hint: il backend ha lanciato un publication_sync
      // in background — informo l'utente cosi' sa che il prossimo
      // push trovera' la cache popolata.
      if (data.auto_sync_triggered) {
        setAutoSyncHint(true);
      }

      // Caso 1: entrambi OK -> success completo.
      if (poleepoOk && miraklOk) {
        onSuccess("ok");
        return;
      }
      // Caso 2: Poleepo non gestisce questo SKU (skipped) + Mirakl OK
      //   -> success "Mirakl direct only", il piu' frequente per
      //   marketplace non integrati su Poleepo (es. MDM, VU).
      if (poleepoSkipped && miraklOk) {
        onSuccess("mirakl_only");
        return;
      }
      // Caso 3: Poleepo OK ma Mirakl fallito -> partial (sync Poleepo
      //   propagara' il prezzo a Mirakl, ritardata).
      if (poleepoOk && !miraklOk) {
        onSuccess("partial");
        return;
      }
      // Casi residui: errore vero (Poleepo HTTP fail, oppure entrambi KO).
      const detail = !poleepoOk
        ? data.poleepo.error || "Errore Poleepo sconosciuto"
        : data.mirakl.error || "Errore Mirakl sconosciuto";
      setError(detail);
    },
    onError: (err: Error) => setError(err.message),
  });

  const diagnose = useMutation({
    mutationFn: () =>
      api.lookupPoleepoPublication(sku, marketplaceId, { maxPages: 30 }),
  });

  const sync = useMutation({
    mutationFn: () => api.syncPoleepoPublications(),
  });

  // Display: il "Prezzo corrente" mostrato e' il PREDEFINITO Mirakl
  // (cosi' Davide vede lo stesso valore che ha sul seller center e
  // capisce subito cosa sta sostituendo). Mostriamo anche il
  // customer-facing min se diverso, perche' e' cosa il cliente paga
  // adesso (puo' essere uno sconto attivo).
  const defaultPriceNum =
    myDefaultPrice !== null && myDefaultPrice !== ""
      ? Number(myDefaultPrice)
      : null;
  const customerPriceNum =
    myPrice !== null && myPrice !== "" ? Number(myPrice) : null;
  const referencePrice = defaultPriceNum ?? customerPriceNum;
  const fmt = (n: number): string => n.toFixed(2);
  const oldPriceLabel =
    defaultPriceNum !== null
      ? fmt(defaultPriceNum)
      : customerPriceNum !== null
        ? fmt(customerPriceNum)
        : "—";
  const customerPriceLabel =
    customerPriceNum !== null && customerPriceNum !== defaultPriceNum
      ? fmt(customerPriceNum)
      : null;
  const delta =
    referencePrice !== null
      ? `${(Number(suggestedPrice) - referencePrice).toFixed(2)}€`
      : "—";

  const showDiagnoseButton =
    error !== null && isPublicationNotMapped(error);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Conferma push prezzo"
      className="fixed inset-0 z-50 flex items-center justify-center bg-rail/40 p-4"
    >
      <div className="w-full max-w-md rounded-[var(--radius-lg)] border border-line-2 bg-card p-6 shadow-panel">
        <h2 className="text-xl font-semibold tracking-tight">Push prezzo manuale</h2>
        <p className="mt-1 text-md text-ink-3">
          <span className="font-code text-ink">{sku}</span> su {marketplaceLabel}
        </p>
        {/* Lo scontrino: da dove si parte, di quanto si muove, dove si arriva. */}
        <dl className="tape mt-4">
          <div className="tape-row">
            <dt>Prezzo predefinito</dt>
            <dd>{oldPriceLabel} €</dd>
          </div>
          {customerPriceLabel && (
            <div className="tape-row">
              <dt title="Cosa il cliente paga oggi (min tra predefinito e sconti attivi)">
                Prezzo customer attuale
              </dt>
              <dd>{customerPriceLabel} €</dd>
            </div>
          )}
          <div className="tape-row">
            <dt>Delta</dt>
            <dd>{delta}</dd>
          </div>
          {expectedMarginPct && (
            <div className="tape-row">
              <dt>Margine atteso</dt>
              <dd>{expectedMarginPct}%</dd>
            </div>
          )}
          <div className="tape-total">
            <dt>Prezzo nuovo</dt>
            <dd>{suggestedPrice} €</dd>
          </div>
        </dl>
        {error && (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        {autoSyncHint && (
          <div
            className="mt-2 rounded-md border border-info/30 bg-info/5 px-3 py-2 text-xs text-info"
            role="status"
          >
            Sync mapping Poleepo avviato in background. Al prossimo push
            su questo SKU lo Step A funzionera' senza skip — vedi pagina
            Storico per lo stato.
          </div>
        )}
        {showDiagnoseButton && !showDiagnostics && (
          <button
            type="button"
            className="mt-2 text-xs underline text-ink-3 hover:text-foreground"
            onClick={() => {
              setShowDiagnostics(true);
              diagnose.mutate();
            }}
          >
            Diagnostica Poleepo (perche' skipped?)
          </button>
        )}
        {showDiagnostics && (
          <DiagnosticsPanel
            data={diagnose.data}
            isPending={diagnose.isPending}
            errorMsg={
              diagnose.error instanceof Error ? diagnose.error.message : null
            }
            onSync={() => sync.mutate()}
            syncPending={sync.isPending}
            syncDone={sync.isSuccess}
          />
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={push.isPending || diagnose.isPending}
            onClick={() => {
              setError(null);
              setAutoSyncHint(false);
              push.mutate();
            }}
          >
            {push.isPending ? "Invio…" : "Conferma push"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DiagnosticsPanelProps {
  data: LookupPublicationResponse | undefined;
  isPending: boolean;
  errorMsg: string | null;
  onSync: () => void;
  syncPending: boolean;
  syncDone: boolean;
}

function DiagnosticsPanel({
  data,
  isPending,
  errorMsg,
  onSync,
  syncPending,
  syncDone,
}: DiagnosticsPanelProps): JSX.Element {
  if (isPending) {
    return (
      <div className="mt-3 rounded-md border border-line bg-paper-2 px-3 py-2 text-xs text-ink-3">
        Diagnosi in corso…
      </div>
    );
  }
  if (errorMsg) {
    return (
      <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
        Diagnosi fallita: {errorMsg}
      </div>
    );
  }
  if (!data) {
    return <></>;
  }

  const tone = diagnosisToTone(data.diagnosis);
  const toneClass =
    tone === "success"
      ? "border-accent/30 bg-accent/5 text-accent-ink"
      : tone === "warning"
        ? "border-warn-gfx/40 bg-warn-gfx/10 text-warn"
        : "border-line bg-paper-2 text-ink-2";

  const canSync =
    data.diagnosis === "not_in_cache_run_sync" ||
    data.diagnosis === "found_live_run_sync" ||
    data.diagnosis === "live_scan_incomplete";

  return (
    <div
      className={`mt-3 rounded-md border px-3 py-2 text-xs ${toneClass}`}
      role="status"
    >
      <div className="font-semibold uppercase tracking-wide">
        {data.diagnosis.replaceAll("_", " ")}
      </div>
      <p className="mt-1 text-ink-2">{data.recommendation}</p>
      {data.live.publications.length > 0 && (
        <ul className="mt-2 space-y-0.5 font-mono text-label text-ink-3">
          {data.live.publications.map((p) => (
            <li key={`${p.publication_id}-${p.source_id}`}>
              #{p.publication_id} · {p.source_id}
              {p.marketplace_id ? ` → ${p.marketplace_id}` : ""}
            </li>
          ))}
        </ul>
      )}
      {canSync && (
        <button
          type="button"
          className="mt-2 btn btn-ghost text-xs"
          onClick={onSync}
          disabled={syncPending || syncDone}
        >
          {syncPending
            ? "Avvio sync…"
            : syncDone
              ? "Sync avviato — vedi pagina Storico"
              : "Sincronizza mapping Poleepo"}
        </button>
      )}
    </div>
  );
}
